
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActionType, DatepickerComponent, FormControlFeedbackComponent, IFilterValue, IFormFieldsValidation, Loader, LoaderSpinnerComponent, UnsubscriberClass, ValidationServiceHelper } from '@aston/foundation';
import { NgbActiveModal, NgbAlertModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, combineLatest } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { UserRole, UserStatus } from '../../enums';
import { ISubsidiaryFormUserDelegations, ISubsidiaryUser } from '../../models';

@Component({
    selector: 'middle-users-delegation-form.component',
    templateUrl: './middle-users-delegation-form.component.html',
    imports: [AsyncPipe, FormsModule, ReactiveFormsModule, NgbAlertModule, NgbTooltipModule, TranslateModule, DatepickerComponent, LoaderSpinnerComponent, FormControlFeedbackComponent]
})
export class MiddleUsersDelegationFormModalComponent extends UnsubscriberClass implements OnInit {
	@Input({ required: true }) loader!: Loader;
	@Input({ required: true }) user: ISubsidiaryUser;
	@Input({ required: true }) users$: Observable<IFilterValue[]> = EMPTY;
	@Input({ required: true }) loadDelegations!: (userId: number) => void;
	@Input({ required: true }) delegations$: Observable<ISubsidiaryFormUserDelegations> = EMPTY;
	@Input({ required: true }) updateDelegations!: (form: ISubsidiaryFormUserDelegations) => void;
	@Input({ required: true }) validationGetter: Observable<[ValidationServiceHelper, IFormFieldsValidation, string]>;
	@Input() mode: ActionType;

	@Output() submitForm: EventEmitter<void> = new EventEmitter();
	@Output() cancelForm: EventEmitter<boolean> = new EventEmitter();

	today = new Date();
	form: UntypedFormGroup;
	ActionType = ActionType;

	validationHelper: ValidationServiceHelper;
	rulesetFields: IFormFieldsValidation;
	userStatus = UserStatus;
	UserRole = UserRole;

	multiSelectConfig = {
		canSelectAll: false,
		labelProp: 'label',
		valueProp: 'value',
	};

	get userRoleField() {
		return this.form?.get('userRoleDelegationIds')
	}

	get superDebtorField() {
		return this.form?.get('superDebtorDelegationIds')
	}

	get endField() {
		return this.form?.get('end')
	}

	constructor(
		public activeModal: NgbActiveModal,
		private fb: UntypedFormBuilder,
	) {
		super();
	}

    ngOnInit() {
		this.form = this.fb.group({});
		this.form.addControl('userRoleDelegationIds', new UntypedFormControl());
		this.form.addControl('superDebtorDelegationIds', new UntypedFormControl());
		this.form.addControl('end', new UntypedFormControl());

		this.validationGetter.pipe(
			tap(([helper, fields]) => {
				this.validationHelper = helper;
				this.rulesetFields = fields;
				this.setFormValidators();
			}),
			takeUntil(this.destroySubscriptions$)
		).subscribe();

		this.delegations$.pipe(
			filter(Boolean),
			takeUntil(this.destroySubscriptions$)
		).subscribe((delegations: ISubsidiaryFormUserDelegations) => {
			if (!delegations.superDebtorDelegationIds.length && !delegations.userRoleDelegationIds.length) {
				this.mode = ActionType.CREATE;
			}
			this.form.patchValue(delegations)
			this.form.markAsPristine()
		})

		combineLatest([
			this.userRoleField.valueChanges,
			this.superDebtorField.valueChanges,
		]).pipe(
			takeUntil(this.destroySubscriptions$)
		).subscribe(_ => {
			if (!this.userRoleField.value?.length && !this.superDebtorField.value?.length) {
				this.endField.setValue(null)
			}
		})

		this.loadDelegations(this.user.userId);
	}

	setFormValidators() {
		this.validationHelper.control(this.endField, this.rulesetFields.End);
	}

	canSubmit() {
		if (this.mode === ActionType.CREATE) {
			return this.endField.value && (this.userRoleField.value.length || this.superDebtorField.value.length);
		}
		return true
	}

	onSubmit() {
		this.updateDelegations({
			...this.form.value,
			userRoleDelegationIds: [+this.form.value.userRoleDelegationIds],
			superDebtorDelegationIds:  [+this.form.value.superDebtorDelegationIds],
			mode: this.mode,
			forUserId: this.user.userId,
		});
	}

	get canDateBeSelected(): boolean {
		// Cannot submit form if no value set
		return !this.userRoleField.value?.length && !this.superDebtorField.value?.length;
	}
}
